/* @refresh reset */
import React from 'react';
import { Badge, Box, Text } from '@chakra-ui/react';
import { formatDate } from '@utils/dateUtils';
import { formatNumberWithCommas } from '@utils';
import { LIBRA_STATUS } from '@constants';

// =============================================================================
// ACCOUNTS TAB COLUMNS
// =============================================================================
export const getAccountsColumns = (onItemClick, selectedItem) => [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    accessorKey: 'partner',
    header: 'Partner',
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    accessorKey: 'currency',
    header: 'Currency',
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    accessorKey: 'accountBalance',
    header: 'Account Balance',
    cell: ({ getValue }) => getValue() || 'N/A'
  }
];


// =============================================================================
// TRANSFERS TAB COLUMNS
// =============================================================================
const TRANSFER_STATUS_COLOR_SCHEME = {
  [LIBRA_STATUS.OPEN]: 'blue',
  [LIBRA_STATUS.PROCESSING]: 'yellow',
  [LIBRA_STATUS.COMPLETED]: 'green',
  [LIBRA_STATUS.FAILED]: 'red',
  [LIBRA_STATUS.CANCELLED]: 'gray'
};

export const getTransfersColumns = (onItemClick, selectedItem) => [
  {
    accessorKey: 'createdAt',
    header: 'Created At',
    cell: ({ getValue }) => formatDate(getValue()),
    sortingFn: 'datetime'
  },
  {
    accessorKey: 'account.name',
    header: 'Sender Account',
    cell: ({ row }) => {
      const accountName = row.original.account?.name || 'N/A';
      const partner = row.original.account?.partner;
      return (
        <Box>
          <Text>{accountName}</Text>
          {partner && (
            <Text fontSize="xs" color="whiteAlpha.600">{partner}</Text>
          )}
        </Box>
      );
    }
  },
  {
    accessorKey: 'corporateBeneficiary.accountName',
    header: 'Receiver Name',
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    accessorKey: 'status',
    header: 'Status',
    filterFn: 'equalsString',
    cell: ({ getValue }) => {
      const status = getValue();
      if (!status) return 'N/A';
      const colorScheme = TRANSFER_STATUS_COLOR_SCHEME[status] || 'gray';
      return (
        <Badge
          colorScheme={colorScheme}
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="md"
        >
          {status}
        </Badge>
      );
    }
  },
  {
    accessorKey: 'mode',
    header: 'Mode',
    cell: ({ getValue }) => getValue() || 'N/A'
  }
];

// =============================================================================
// CORPORATE BENEFICIARIES TAB COLUMNS
// =============================================================================
export const getCorporateBeneficiariesColumns = (onItemClick, selectedItem) => [
  {
    accessorKey: 'accountName',
    header: 'Account Name',
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    accessorKey: 'isActive',
    header: 'Status',
    cell: ({ getValue }) => {
      const isActive = getValue();
      return (
        <Badge
          colorScheme={isActive ? 'green' : 'gray'}
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="md"
        >
          {isActive ? 'Active' : 'Inactive'}
        </Badge>
      );
    }
  },
  {
    accessorKey: 'currency',
    header: 'Currency',
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    accessorKey: 'corporateBeneficiaryDetails.transferType',
    header: 'Transfer Type',
    cell: ({ row }) => row.original.corporateBeneficiaryDetails?.transferType || 'N/A'
  },
  {
    accessorKey: 'createdAt',
    header: 'Created At',
    cell: ({ getValue }) => formatDate(getValue())
  }
];

// =============================================================================
// SCHEDULED TRANSFERS TAB COLUMNS
// =============================================================================
export const getScheduledTransfersColumns = (onItemClick, selectedItem) => [
  {
    accessorKey: 'account.name',
    header: 'Account Name',
    cell: ({ row }) => {
      const accountName = row.original.account?.name || 'N/A';
      const partner = row.original.account?.partner;
      return (
        <Box>
          <Text>{accountName}</Text>
          {partner && (
            <Text fontSize="xs" color="whiteAlpha.600">{partner}</Text>
          )}
        </Box>
      );
    }
  },
  {
    accessorKey: 'amountSpec.amountType',
    header: 'Amount',
    cell: ({ row }) => {
      const amountType = row.original.amountSpec?.amountType;
      if (amountType === 'AVAILABLE_BALANCE') {
        return 'Full Balance';
      } else if (amountType === 'FIXED') {
        const amount = row.original.amountSpec?.amount;
        return amount !== undefined && amount !== null ? formatNumberWithCommas(amount) + ' ' + (row.original.account?.currency || '') : 'N/A';
      }
      return 'N/A';
    }
  },
  {
    accessorKey: 'scheduledTransferType',
    header: 'Scheduled Transfer Type',
    cell: ({ getValue }) => getValue() || 'N/A'
  }
];
